/**
 * Hook: useDealImport
 *
 * Centraliza chamadas RPC do fluxo de importação em massa de negócios.
 * Toda comunicação com o banco passa por funções SECURITY DEFINER (sem SELECT direto).
 *
 * Fluxo:
 *   1. createJob          - cria registro draft com mapping/defaults/options
 *   2. populateItems      - envia rows do CSV em lotes (frontend pagina)
 *   3. validateJob        - dry-run server-side, classifica cada item
 *   4. approveAndEnqueue  - aprovação explícita, enfileira batches PGMQ
 *   5. cancelJob          - cancelamento manual
 *   6. getJobDetails      - polling de progresso
 *
 * RELEASE: 2026-05-15 v1.0.0
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const log = createLogger('useDealImport');

// ============= Tipos =============
export interface DealImportMapping {
  [csvColumn: string]: string; // csv_column -> deal_field
}

export interface DealImportDefaults {
  pipeline_id?: string;
  phase_id?: string;
  source?: string;
  seller_id?: string | null;
  workspace_id?: string;
  // v1.2.0 - Modo de vendedor (Manual | Fila | Vindo do arquivo)
  seller_mode?: 'manual' | 'queue' | 'from_file';
  distribution_queue_id?: string | null;
}

export interface DealImportOptions {
  silent_mode?: boolean;            // padrão TRUE
  auto_distribute?: boolean;        // padrão FALSE
  allow_duplicates_in_csv?: boolean;
  update_existing_contact?: boolean;
  default_country_code?: string;    // padrão "55"
  duplicate_behavior?: 'skip' | 'update' | 'create_new';
  duplicate_window_days?: number;   // padrão 90
  // v1.2.0 - Toggle "criar tags automaticamente se não existirem"
  auto_create_tags?: boolean;
}

export interface ValidationCounters {
  total_rows: number;
  valid_count: number;
  invalid_count: number;
  duplicate_count: number;
  blacklisted_count: number;
}

export interface DealImportJobDetails {
  job: any;
  batches_summary: {
    total: number;
    pending: number;
    enqueued: number;
    processing: number;
    completed: number;
    failed: number;
    stalled?: number;
  };
  last_batch_update?: string | null;
  last_batch_error?: string | null;
}

// ============= Hook =============
export function useDealImport() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;
  const qc = useQueryClient();

  // 1) Criar job
  const createJob = useMutation({
    mutationFn: async (input: {
      name: string;
      source_file_path?: string;
      source_file_name?: string;
      mapping: DealImportMapping;
      defaults: DealImportDefaults;
      options: DealImportOptions;
      total_rows: number;
    }) => {
      const { data, error } = await supabase.rpc('create_deal_import_job', {
        p_name: input.name,
        p_source_file_path: input.source_file_path ?? null,
        p_source_file_name: input.source_file_name ?? null,
        p_mapping: input.mapping as any,
        p_defaults: input.defaults as any,
        p_options: input.options as any,
        p_total_rows: input.total_rows,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
      return data as string; // job_id
    },
  });

  // 2) Popular itens (rows do CSV) em lotes
  const populateItems = useMutation({
    mutationFn: async (input: {
      jobId: string;
      rows: Array<{ row_index: number; raw_row: Record<string, unknown> }>;
    }) => {
      const CHUNK = 500;
      let total = 0;
      for (let i = 0; i < input.rows.length; i += CHUNK) {
        const slice = input.rows.slice(i, i + CHUNK);
        const { data, error } = await supabase.rpc('populate_deal_import_items', {
          p_job_id: input.jobId,
          p_rows: slice as any,
          p_tenant_hint: tenantId,
        });
        if (error) throw error;
        total += (data as number) ?? 0;
        log.debug(`populated chunk ${i / CHUNK + 1}: ${data} rows`);
      }
      return total;
    },
  });

  // 3) Validar (dry-run) — itera em CHUNKS no servidor para evitar statement_timeout
  //    em importações grandes (10k, 20k+). A RPC v1.0.5 aceita p_chunk_size:
  //    cada chamada processa N itens 'pending' e devolve os contadores totais.
  //    Continuamos chamando até (valid+invalid+duplicate+blacklisted) == total_rows.
  // v1.0.6 - Suporte a callback onProgress para alimentar UI com contadores
  //          parciais (Step4Validating mostra barra de progresso ao vivo).
  const validateJob = useMutation({
    mutationFn: async (
      input:
        | string
        | {
            jobId: string;
            allowedSources?: string[];
            chunkSize?: number;
            onProgress?: (counters: ValidationCounters) => void;
          },
    ) => {
      const jobId = typeof input === 'string' ? input : input.jobId;
      const allowedSources = typeof input === 'string' ? undefined : input.allowedSources;
      const chunkSize = (typeof input === 'string' ? undefined : input.chunkSize) ?? 500;
      const onProgress = typeof input === 'string' ? undefined : input.onProgress;

      let last: ValidationCounters | null = null;
      const MAX_ITERATIONS = 500; // guarda contra loop infinito (500 * 500 = 250k)
      for (let i = 0; i < MAX_ITERATIONS; i++) {
        const { data, error } = await supabase.rpc('validate_deal_import_job', {
          p_job_id: jobId,
          p_tenant_hint: tenantId,
          p_chunk_size: chunkSize,
          ...(allowedSources ? { p_allowed_sources: allowedSources } : {}),
        } as any);
        if (error) throw error;
        const row = (Array.isArray(data) ? data[0] : data) as ValidationCounters;
        last = row;
        const processed =
          (row?.valid_count ?? 0) +
          (row?.invalid_count ?? 0) +
          (row?.duplicate_count ?? 0) +
          (row?.blacklisted_count ?? 0);
        log.debug(`validate chunk ${i + 1}: processed=${processed}/${row?.total_rows ?? 0}`);
        // Reporta progresso parcial à UI (Step4Validating)
        try { onProgress?.(row); } catch { /* noop */ }
        if (processed >= (row?.total_rows ?? 0)) break;
      }
      if (!last) throw new Error('Validação não retornou contadores');
      return last;
    },
  });

  // 4) Aprovar e enfileirar
  const approveAndEnqueue = useMutation({
    mutationFn: async (jobId: string) => {
      log.info('rpc approve_and_enqueue_deal_import_job: chamando', { jobId, tenantId });
      // v1.2.0: batch reduzido para 25 itens — maior granularidade no worker
      // (process_deal_import_batch com lock atômico), reduz risco de timeout
      // em importações com criação pesada (contato + deal + tags + fila) e
      // melhora a atualização incremental da barra de progresso.
      // v1.1.0: batch reduzido para 100 itens (histórico).
      const { data, error } = await supabase.rpc(
        'approve_and_enqueue_deal_import_job',
        {
          p_job_id: jobId,
          p_batch_size: 25,
          p_tenant_hint: tenantId,
        },
      );
      if (error) {
        log.error('rpc approve_and_enqueue_deal_import_job: erro', {
          jobId,
          message: error.message,
          code: (error as any).code,
          details: (error as any).details,
          hint: (error as any).hint,
        });
        throw error;
      }
      const row = Array.isArray(data) ? data[0] : data;
      log.info('rpc approve_and_enqueue_deal_import_job: ok', { jobId, row });
      return row as { job_id: string; batches_created: number; items_enqueued: number };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deal-import-jobs'] }),
  });

  // 5) Cancelar
  const cancelJob = useMutation({
    mutationFn: async (jobId: string) => {
      const { error } = await supabase.rpc('cancel_deal_import_job', {
        p_job_id: jobId,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deal-import-jobs'] }),
  });

  // 5.1) Excluir definitivamente um job (e seus itens).
  //      RPC valida permissão granular crm_deals_import + delete.
  const deleteJob = useMutation({
    mutationFn: async (jobId: string) => {
      const { error } = await supabase.rpc('delete_deal_import_job', {
        p_job_id: jobId,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deal-import-jobs'] }),
  });

  // 6) Detalhes (polling)
  function useJobDetails(jobId: string | null, options?: { refetchInterval?: number | ((q: any) => number | false) }) {
    return useQuery({
      queryKey: ['deal-import-job', jobId],
      enabled: !!jobId && !!tenantId,
      // Polling dinâmico: 3s enquanto houver lote pendente, 15s quando concluído
      refetchInterval: options?.refetchInterval ?? ((query: any) => {
        const d: any = query?.state?.data;
        const status = d?.job?.status;
        if (status === 'completed' || status === 'completed_with_errors' || status === 'failed' || status === 'cancelled') {
          return 15000;
        }
        return 3000;
      }),
      queryFn: async () => {
        const { data, error } = await supabase.rpc('get_deal_import_job_details', {
          p_job_id: jobId!,
          p_tenant_hint: tenantId,
        });
        if (error) throw error;
        return data as unknown as DealImportJobDetails;
      },
    });
  }

  // 7) Reprocessar 1 item (failed/skipped/invalid/duplicate/blacklisted)
  const requeueItem = useMutation({
    mutationFn: async (itemId: string) => {
      const { data, error } = await supabase.rpc('requeue_deal_import_item', {
        p_item_id: itemId,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
      return data as string; // batch_id criado
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deal-import-job'] });
      qc.invalidateQueries({ queryKey: ['deal-import-items'] });
    },
  });

  // 8) Listar itens do job (filtrável por status). Server-side via RPC
  //    get_deal_import_job_items (SECURITY DEFINER + validação de tenant).
  //    v1.0.2: suporta p_offset e devolve total_count para paginação real.
  function useJobItems(
    jobId: string | null,
    statuses?: string[],
    options?: { limit?: number; offset?: number; refetchInterval?: number; enabled?: boolean },
  ) {
    const limit = options?.limit ?? 500;
    const offset = options?.offset ?? 0;
    return useQuery({
      queryKey: ['deal-import-items', tenantId, jobId, statuses?.join(',') ?? 'all', limit, offset],
      enabled: !!jobId && (options?.enabled ?? true),
      refetchInterval: options?.refetchInterval,
      queryFn: async () => {
        const { data, error } = await supabase.rpc('get_deal_import_job_items', {
          p_job_id: jobId!,
          p_statuses: statuses && statuses.length > 0 ? statuses : null,
          p_limit: limit,
          p_offset: offset,
          p_tenant_hint: tenantId,
        } as any);
        if (error) throw error;
        return ((data ?? []) as any[]) as Array<{
          id: string;
          row_index: number;
          status: string;
          raw_row: Record<string, unknown>;
          validation_errors: any;
          last_error: string | null;
          resolved_deal_id: string | null;
          total_count: number;
        }>;
      },
    });
  }

  /**
   * Busca TODAS as linhas problemáticas paginando em lotes (default 1000).
   * Usado pelo botão "Baixar CSV de erros" para superar o limite default
   * de 1000 rows do PostgREST quando há > 1k inválidas.
   *
   * Hard-stop de 50 páginas (50k linhas) por segurança.
   */
  async function fetchAllJobItems(
    jobId: string,
    statuses: string[],
    pageSize = 1000,
  ): Promise<Array<{
    id: string;
    row_index: number;
    status: string;
    raw_row: Record<string, unknown>;
    validation_errors: any;
    last_error: string | null;
    resolved_deal_id: string | null;
  }>> {
    const all: any[] = [];
    let offset = 0;
    let total = Infinity;
    const MAX_PAGES = 50;

    for (let i = 0; i < MAX_PAGES && all.length < total; i += 1) {
      const { data, error } = await supabase.rpc('get_deal_import_job_items', {
        p_job_id: jobId,
        p_statuses: statuses.length > 0 ? statuses : null,
        p_limit: pageSize,
        p_offset: offset,
        p_tenant_hint: tenantId,
      } as any);
      if (error) throw error;
      const page = (data ?? []) as any[];
      if (page.length === 0) break;
      total = Number(page[0]?.total_count ?? page.length);
      all.push(...page);
      offset += page.length;
      if (page.length < pageSize) break;
    }

    return all;
  }

  // 9) Prévia paginada de linhas problemáticas (invalid/duplicate/blacklisted)
  //    Consome RPC SECURITY DEFINER get_deal_import_invalid_items, que retorna
  //    também total_count para suportar paginação "Carregar mais".
  function useInvalidItems(
    jobId: string | null,
    options?: { limit?: number; offset?: number; enabled?: boolean },
  ) {
    const limit = options?.limit ?? 50;
    const offset = options?.offset ?? 0;
    return useQuery({
      queryKey: ['deal-import-invalid-items', jobId, limit, offset],
      enabled: !!jobId && (options?.enabled ?? true),
      queryFn: async () => {
        const { data, error } = await supabase.rpc('get_deal_import_invalid_items', {
          p_job_id: jobId!,
          p_limit: limit,
          p_offset: offset,
          p_tenant_hint: tenantId,
        } as any);
        if (error) throw error;
        // RPC v1.0.1 retorna colunas com prefixo `out_` para evitar colisão
        // com parâmetros OUT do PostgreSQL. Mapeamos aqui para manter a
        // interface estável para os componentes consumidores.
        const raw = (data ?? []) as Array<{
          out_id: string;
          out_row_index: number;
          out_status: string;
          out_raw_row: Record<string, unknown>;
          out_validation_errors: any;
          out_last_error: string | null;
          out_total_count: number;
        }>;
        const rows = raw.map((r) => ({
          id: r.out_id,
          row_index: r.out_row_index,
          status: r.out_status,
          raw_row: r.out_raw_row,
          validation_errors: r.out_validation_errors,
          last_error: r.out_last_error,
          total_count: Number(r.out_total_count ?? 0),
        }));
        return {
          items: rows,
          total: rows[0]?.total_count ?? 0,
        };
      },
    });
  }

  return {
    createJob,
    populateItems,
    validateJob,
    approveAndEnqueue,
    cancelJob,
    deleteJob,
    useJobDetails,
    requeueItem,
    useJobItems,
    fetchAllJobItems,
    useInvalidItems,
  };
}
